import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSIPProvider, CONNECT_STATUS } from "react-sipjs";
import { FetchUserExtension } from "../helper/Constants";

const SIP_ENABLED_ROUTES = new Set([
  "/live-call",
  "/campaign-live-call",
  "/extension-live-calls",
  "/campaign-live-calls",
]);

const INITIAL_RETRY_DELAY_MS = 3000;
const MAX_RETRY_DELAY_MS = 30000;

const SIPAdminHandler = () => {
  const location = useLocation();
  const { connectAndRegister, connectStatus } = useSIPProvider();
  const [sipCredentials, setSipCredentials] = useState(null);
  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const isConnectingRef = useRef(false);
  const isFetchingCredentialsRef = useRef(false);
  const connectStatusRef = useRef(connectStatus);

  const shouldConnect = SIP_ENABLED_ROUTES.has(location.pathname);

  const clearRetryTimeout = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const connectToSip = useCallback(async (credentials) => {
    if (!credentials?.username || !credentials?.password) {
      console.error("Missing SIP credentials:", credentials);
      return;
    }

    if (connectStatusRef.current === CONNECT_STATUS.CONNECTED || isConnectingRef.current) {
      return;
    }

    isConnectingRef.current = true;

    try {
      await connectAndRegister({
        username: credentials.username,
        password: credentials.password,
      });
    } catch (error) {
      console.error("SIP connection error:", error);
    } finally {
      isConnectingRef.current = false;
    }
  }, [connectAndRegister]);

  const scheduleReconnect = useCallback((credentials) => {
    if (
      !shouldConnect ||
      !credentials?.username ||
      !credentials?.password ||
      connectStatusRef.current === CONNECT_STATUS.CONNECTED ||
      retryTimeoutRef.current
    ) {
      return;
    }

    const delay = Math.min(
      INITIAL_RETRY_DELAY_MS * (2 ** retryCountRef.current),
      MAX_RETRY_DELAY_MS
    );

    retryTimeoutRef.current = setTimeout(() => {
      retryTimeoutRef.current = null;

      if (!shouldConnect || connectStatusRef.current === CONNECT_STATUS.CONNECTED) {
        return;
      }

      retryCountRef.current += 1;
      connectToSip(credentials);
      scheduleReconnect(credentials);
    }, delay);
  }, [connectToSip, shouldConnect]);

  useEffect(() => {
    connectStatusRef.current = connectStatus;

    if (connectStatus === CONNECT_STATUS.CONNECTED) {
      retryCountRef.current = 0;
      clearRetryTimeout();
    }
  }, [clearRetryTimeout, connectStatus]);

  useEffect(() => {
    if (!shouldConnect || sipCredentials || isFetchingCredentialsRef.current) {
      return;
    }

    let isCancelled = false;

    const fetchAdminExtension = async () => {
      isFetchingCredentialsRef.current = true;

      try {
        const response = await FetchUserExtension();

        if (isCancelled) {
          return;
        }

        if (response.status && response.data?.username && response.data?.password) {
          setSipCredentials({
            username: response.data.username,
            password: response.data.password,
          });
          retryCountRef.current = 0;
        } else {
          console.error("Invalid SIP credentials:", response);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Extension fetch error:", error);
        }
      } finally {
        isFetchingCredentialsRef.current = false;
      }
    };

    fetchAdminExtension();

    return () => {
      isCancelled = true;
    };
  }, [shouldConnect, sipCredentials]);

  useEffect(() => {
    if (!shouldConnect) {
      clearRetryTimeout();
      retryCountRef.current = 0;
      return;
    }

    if (!sipCredentials) {
      return;
    }

    if (connectStatus === CONNECT_STATUS.CONNECTED) {
      return;
    }

    if (connectStatus === CONNECT_STATUS.WAIT_REQUEST_CONNECT) {
      connectToSip(sipCredentials);
    }

    scheduleReconnect(sipCredentials);
  }, [
    clearRetryTimeout,
    connectStatus,
    connectToSip,
    scheduleReconnect,
    shouldConnect,
    sipCredentials,
  ]);

  useEffect(() => () => {
    clearRetryTimeout();
  }, [clearRetryTimeout]);

  return null;
};

export default SIPAdminHandler;
